import { useState, useEffect, useRef } from "react";
import vincentPhoto from './assets/vincent.png';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;600;700;900&display=swap');

  :root {
    --bg: #020810;
    --accent: #00aaff;
    --glow: #38c8ff;
    --orange: #ff8c00;
    --orange-glow: #ffaa33;
    --white: #e8f4ff;
    --muted: #4a7a9b;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: var(--bg); }

  .portfolio-root {
    font-family: 'Rajdhani', sans-serif;
    background: var(--bg);
    color: var(--white);
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
  }

  .grid-bg {
    position: fixed; inset: 0;
    background-image:
      linear-gradient(rgba(0,170,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,170,255,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    z-index: 0; pointer-events: none;
  }

  .canvas-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none; }

  .center-glow {
    position: fixed; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 700px; height: 700px;
    background: radial-gradient(circle, rgba(0,80,180,0.12) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; justify-content: space-between; align-items: center;
    padding: 1.2rem 3rem;
    background: rgba(2,8,16,0.92);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0,170,255,0.1);
  }

  .nav-logo {
    font-family: 'Orbitron', monospace; font-size: 1.2rem; font-weight: 700;
    color: var(--accent); letter-spacing: 3px;
    text-shadow: 0 0 20px rgba(0,170,255,0.8);
    cursor: pointer;
  }

  .nav-links { display: flex; gap: 2.5rem; list-style: none; }

  .nav-links a {
    font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 600;
    color: var(--muted); text-decoration: none; letter-spacing: 2px;
    text-transform: uppercase; transition: color 0.3s; position: relative;
    cursor: pointer;
  }

  .nav-links a:hover { color: var(--accent); }

  .nav-links a::after {
    content: ''; position: absolute; bottom: -4px; left: 0;
    width: 0; height: 1px; background: var(--accent);
    transition: width 0.3s; box-shadow: 0 0 6px var(--accent);
  }
  .nav-links a:hover::after { width: 100%; }

  .hero {
    position: relative; z-index: 1; min-height: 100vh;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 100px 2rem 2rem; text-align: center;
  }

  .hero-eyebrow {
    font-family: 'Orbitron', monospace; font-size: 0.65rem; font-weight: 600;
    color: var(--orange-glow); letter-spacing: 5px; text-transform: uppercase;
    margin-bottom: 1.8rem; opacity: 0; animation: fadeDown 0.8s ease 0.2s both;
    display: flex; align-items: center; gap: 1rem;
  }

  .hero-eyebrow::before, .hero-eyebrow::after { content: ''; width: 50px; height: 1px; }
  .hero-eyebrow::before { background: linear-gradient(to right, transparent, var(--orange)); }
  .hero-eyebrow::after { background: linear-gradient(to left, transparent, var(--orange)); }

  .photo-wrap {
    position: relative; margin-bottom: 2.5rem;
    opacity: 0; animation: fadeUp 1s ease 0.4s both;
  }

  .photo-outer-ring {
    position: absolute; inset: -28px; border-radius: 50%;
    border: 1px solid rgba(0,170,255,0.2);
    animation: spinSlow 15s linear infinite;
  }

  .photo-outer-ring::before {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: var(--orange);
    border-radius: 50%; box-shadow: 0 0 12px var(--orange); transform: translateX(-50%);
  }

  .photo-outer-ring::after {
    content: ''; position: absolute; bottom: -4px; left: 50%;
    width: 5px; height: 5px; background: var(--accent);
    border-radius: 50%; box-shadow: 0 0 8px var(--accent); transform: translateX(-50%);
  }

  .photo-inner-ring {
    position: absolute; inset: -12px; border-radius: 50%;
    border: 1px dashed rgba(255,140,0,0.15);
    animation: spinSlow 10s linear infinite reverse;
  }

  .photo-circle {
    width: 200px; height: 200px; border-radius: 50%; overflow: hidden;
    border: 2px solid rgba(0,170,255,0.6);
    box-shadow: 0 0 0 4px rgba(0,170,255,0.08), 0 0 40px rgba(0,170,255,0.4), 0 0 80px rgba(0,170,255,0.12);
    position: relative; z-index: 1;
  }

  .photo-circle img {
    width: 100%; height: 150%; object-fit: cover; object-position: center 10%;
    transform: scale(1.3) translateY(-12%);
    filter: brightness(1.05) contrast(1.1) saturate(1.1);
  }

  .photo-glow-outer {
    position: absolute; inset: -40px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,100,255,0.12) 0%, transparent 70%);
    pointer-events: none;
  }

  .status-badge {
    position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%);
    background: rgba(2,8,16,0.97); border: 1px solid rgba(255,140,0,0.45);
    border-radius: 20px; padding: 0.35rem 1rem;
    display: flex; align-items: center; gap: 0.5rem;
    font-size: 0.65rem; font-weight: 600; color: var(--orange-glow);
    letter-spacing: 1px; white-space: nowrap; z-index: 2;
    box-shadow: 0 0 15px rgba(255,140,0,0.2);
  }

  .status-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--orange); box-shadow: 0 0 8px var(--orange);
    animation: pulse 2s infinite;
  }

  .hero-name {
    font-family: 'Orbitron', monospace; font-size: clamp(2rem, 5vw, 3.6rem);
    font-weight: 900; line-height: 1.1; margin-bottom: 0.8rem;
    opacity: 0; animation: fadeUp 0.8s ease 0.6s both;
    background: linear-gradient(135deg, #ffffff 0%, var(--glow) 50%, var(--accent) 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; filter: drop-shadow(0 0 30px rgba(0,170,255,0.4));
  }

  .hero-title {
    font-size: 0.9rem; font-weight: 500; color: var(--muted);
    letter-spacing: 3px; text-transform: uppercase; margin-bottom: 1.5rem;
    opacity: 0; animation: fadeUp 0.8s ease 0.8s both;
  }
  .hero-title span { color: var(--accent); }

  .hero-bio {
    font-size: 1rem; line-height: 1.8; color: rgba(232,244,255,0.5);
    max-width: 520px; margin: 0 auto 2rem;
    opacity: 0; animation: fadeUp 0.8s ease 1s both;
  }

  .hero-cta {
    display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;
    margin-bottom: 3rem; opacity: 0; animation: fadeUp 0.8s ease 1.2s both;
  }

  .btn-primary {
    padding: 0.75rem 2rem;
    background: linear-gradient(135deg, var(--orange), #cc5500);
    color: #fff; font-family: 'Rajdhani', sans-serif; font-size: 0.9rem;
    font-weight: 700; letter-spacing: 2px; text-transform: uppercase; border: none;
    cursor: pointer; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%);
    box-shadow: 0 0 20px rgba(255,140,0,0.35); transition: box-shadow 0.3s, transform 0.2s;
  }
  .btn-primary:hover { box-shadow: 0 0 35px rgba(255,140,0,0.7); transform: translateY(-2px); }

  .btn-secondary {
    padding: 0.75rem 2rem; background: transparent; color: var(--accent);
    font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase; border: 1px solid rgba(0,170,255,0.35);
    cursor: pointer; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%);
    transition: background 0.3s, box-shadow 0.3s, transform 0.2s;
  }
  .btn-secondary:hover { background: rgba(0,170,255,0.08); box-shadow: 0 0 20px rgba(0,170,255,0.25); transform: translateY(-2px); }

  .stat-row {
    display: flex; gap: 1px; justify-content: center;
    opacity: 0; animation: fadeUp 0.8s ease 1.4s both;
  }

  .stat-chip {
    display: flex; flex-direction: column; align-items: center;
    padding: 0.8rem 2.2rem;
    background: rgba(5,15,31,0.85); border: 1px solid rgba(0,170,255,0.1);
    position: relative;
  }

  .stat-chip::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent, var(--accent), transparent);
  }

  .stat-number {
    font-family: 'Orbitron', monospace; font-size: 1rem;
    font-weight: 700; color: var(--orange-glow);
  }

  .stat-label {
    font-size: 0.6rem; color: var(--muted); letter-spacing: 2px;
    text-transform: uppercase; margin-top: 0.3rem;
  }

  .side-icons-left, .side-icons-right {
    position: absolute; top: 50%; transform: translateY(-50%);
    display: flex; flex-direction: column; gap: 1.2rem; z-index: 2;
  }
  .side-icons-left { left: 2.5rem; }
  .side-icons-right { right: 2.5rem; }

  .side-icon {
    width: 44px; height: 44px; background: rgba(5,15,31,0.9);
    border: 1px solid rgba(0,170,255,0.2); border-radius: 8px;
    display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
    animation: floatIcon 4s ease-in-out infinite;
    transition: border-color 0.3s, box-shadow 0.3s;
  }
  .side-icon:hover { border-color: rgba(0,170,255,0.6); box-shadow: 0 0 15px rgba(0,170,255,0.3); }
  .side-icon:nth-child(2) { animation-delay: 0.6s; }
  .side-icon:nth-child(3) { animation-delay: 1.2s; }
  .side-icon:nth-child(4) { animation-delay: 1.8s; }

  .corner { position: fixed; width: 50px; height: 50px; z-index: 1; pointer-events: none; }
  .corner-tl { top: 75px; left: 15px; border-top: 1px solid rgba(0,170,255,0.25); border-left: 1px solid rgba(0,170,255,0.25); }
  .corner-tr { top: 75px; right: 15px; border-top: 1px solid rgba(0,170,255,0.25); border-right: 1px solid rgba(0,170,255,0.25); }
  .corner-bl { bottom: 15px; left: 15px; border-bottom: 1px solid rgba(0,170,255,0.25); border-left: 1px solid rgba(0,170,255,0.25); }
  .corner-br { bottom: 15px; right: 15px; border-bottom: 1px solid rgba(0,170,255,0.25); border-right: 1px solid rgba(0,170,255,0.25); }

  .scroll-indicator {
    position: absolute; bottom: 2rem; left: 50%; transform: translateX(-50%);
    display: flex; flex-direction: column; align-items: center; gap: 0.4rem;
    opacity: 0; animation: fadeUp 1s ease 2s both;
  }
  .scroll-label { font-size: 0.6rem; letter-spacing: 3px; text-transform: uppercase; color: var(--muted); }
  .scroll-line {
    width: 1px; height: 35px;
    background: linear-gradient(to bottom, var(--accent), transparent);
    animation: scrollPulse 1.5s ease-in-out infinite;
  }

  @keyframes fadeDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.7); } }
  @keyframes floatIcon { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes scrollPulse {
    0% { opacity: 0; transform: scaleY(0); transform-origin: top; }
    50% { opacity: 1; transform: scaleY(1); transform-origin: top; }
    100% { opacity: 0; transform: scaleY(1); transform-origin: bottom; }
  }

  @media (max-width: 900px) { .side-icons-left, .side-icons-right { display: none; } .nav { padding: 1rem 1.5rem; } .nav-links { gap: 1.2rem; } }
  @media (max-width: 600px) { .hero-name { font-size: 1.8rem; } .stat-chip { padding: 0.6rem 1.2rem; } }
`;

function NetworkCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, t = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const codeChars = "01アイウエオカキABCDEFGHIJ{}[]()<>/\\;:=+-*&%$#@!?";
    const cols = Math.floor(window.innerWidth / 22);
    const drops = Array.from({ length: cols }, () => Math.random() * -100);
    const speeds = Array.from({ length: cols }, () => 0.25 + Math.random() * 0.4);

    const blocks = Array.from({ length: 5 }, (_, i) => ({
      x: (window.innerWidth / 6) * (i + 0.5) + (Math.random() - 0.5) * 60,
      y: 120 + Math.random() * (window.innerHeight - 320),
      w: 130 + Math.random() * 90,
      h: 90 + Math.random() * 110,
      alpha: 0.04 + Math.random() * 0.05,
      vy: (Math.random() - 0.5) * 0.15,
      lines: Array.from({ length: 9 }, () =>
        Array.from({ length: Math.floor(8 + Math.random() * 10) }, () =>
          codeChars[Math.floor(Math.random() * codeChars.length)]
        ).join("")
      ),
    }));

    const nodes = Array.from({ length: 35 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      r: Math.random() * 1.5 + 0.4,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.012;

      ctx.font = "12px monospace";
      drops.forEach((y, i) => {
        const x = i * 22;
        ctx.fillStyle = "rgba(120, 230, 255, 0.85)";
        ctx.fillText(codeChars[Math.floor(Math.random() * codeChars.length)], x, y * 20);
        for (let k = 1; k < 10; k++) {
          const alpha = (1 - k / 10) * 0.1;
          ctx.fillStyle = `rgba(0, 170, 255, ${alpha})`;
          ctx.fillText(codeChars[Math.floor(Math.random() * codeChars.length)], x, (y - k) * 20);
        }
        if (y * 20 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i] += speeds[i];
      });

      blocks.forEach((b) => {
        b.y += b.vy;
        if (b.y < 60 || b.y > canvas.height - 180) b.vy *= -1;
        const bx = b.x - b.w / 2, by = b.y - b.h / 2;
        ctx.fillStyle = `rgba(0, 20, 60, ${b.alpha + Math.sin(t) * 0.01})`;
        ctx.fillRect(bx, by, b.w, b.h);
        ctx.strokeStyle = `rgba(0, 170, 255, 0.14)`;
        ctx.lineWidth = 1;
        ctx.strokeRect(bx, by, b.w, b.h);
        const cs = 8;
        ctx.strokeStyle = `rgba(0, 210, 255, 0.5)`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(bx + cs, by); ctx.lineTo(bx, by); ctx.lineTo(bx, by + cs);
        ctx.moveTo(bx + b.w - cs, by); ctx.lineTo(bx + b.w, by); ctx.lineTo(bx + b.w, by + cs);
        ctx.moveTo(bx, by + b.h - cs); ctx.lineTo(bx, by + b.h); ctx.lineTo(bx + cs, by + b.h);
        ctx.moveTo(bx + b.w - cs, by + b.h); ctx.lineTo(bx + b.w, by + b.h); ctx.lineTo(bx + b.w, by + b.h - cs);
        ctx.stroke();
        ctx.font = "7.5px monospace";
        b.lines.forEach((line, li) => {
          const lineAlpha = 0.12 + Math.sin(t * 1.5 + li * 0.8) * 0.06;
          ctx.fillStyle = `rgba(0, 200, 255, ${lineAlpha})`;
          ctx.fillText(line, bx + 7, by + 14 + li * 10);
        });
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0,150,255,${0.07 * (1 - dist / 110)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 180, 255, 0.35)";
        ctx.fill();
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="canvas-bg" />;
}

// Smooth scroll helper
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Home() {
  const [imgSrc] = useState(vincentPhoto);

  return (
    <div className="portfolio-root">
      <style>{styles}</style>
      <div className="grid-bg" />
      <NetworkCanvas />
      <div className="center-glow" />
      <div className="corner corner-tl" />
      <div className="corner corner-tr" />
      <div className="corner corner-bl" />
      <div className="corner corner-br" />

      {/* NAV */}
      <nav className="nav">
        <div className="nav-logo" onClick={() => scrollTo("home")}>VM</div>
        <ul className="nav-links">
          {[
            { label: "Home",     id: "home"     },
            { label: "About",    id: "about"    },
            { label: "Skills",   id: "skills"   },
            { label: "Projects", id: "projects" },
            { label: "Contact",  id: "contact"  },
          ].map(({ label, id }) => (
            <li key={id}>
              <a onClick={(e) => { e.preventDefault(); scrollTo(id); }} href={`#${id}`}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <section className="hero" id="home">
        <div className="side-icons-left">
          <div className="side-icon">💻</div>
          <div className="side-icon">🔒</div>
          <div className="side-icon">⚡</div>
          <div className="side-icon">🖥️</div>
        </div>

        <p className="hero-eyebrow">BS Computer Engineering</p>

        <div className="photo-wrap">
          <div className="photo-outer-ring" />
          <div className="photo-inner-ring" />
          <div className="photo-glow-outer" />
          <div className="photo-circle">
            <img src={imgSrc} alt="Vincent D. Manalo" />
          </div>
          <div className="status-badge">
            <div className="status-dot" />
            Ready for New Opportunities
          </div>
        </div>

        <h1 className="hero-name">Vincent D. Manalo</h1>

        <p className="hero-title">
          <span>Aspiring Software Developer</span> &nbsp;·&nbsp; Tech Enthusiast
        </p>

        <p className="hero-bio">
          A Computer Engineering student with a passion for technology and problem-solving,
          driven to build innovative solutions that make a meaningful impact.
        </p>

        <div className="hero-cta">
          <button className="btn-primary"  onClick={() => scrollTo("resume")}>My Resume</button>
          <button className="btn-secondary" onClick={() => scrollTo("about")}>About Me</button>
          <button className="btn-secondary" onClick={() => scrollTo("projects")}>View Projects</button>
        </div>

        <div className="stat-row">
          <div className="stat-chip">
            <span className="stat-number">3RD YR</span>
            <span className="stat-label">CPE Student</span>
          </div>
          <div className="stat-chip">
            <span className="stat-number">10+</span>
            <span className="stat-label">Projects</span>
          </div>
          <div className="stat-chip">
            <span className="stat-number">PH</span>
            <span className="stat-label">Based</span>
          </div>
        </div>

        <div className="side-icons-right">
          <div className="side-icon">☁️</div>
          <div className="side-icon">🧠</div>
          <div className="side-icon">📡</div>
          <div className="side-icon">🔧</div>
        </div>

        <div className="scroll-indicator">
          <span className="scroll-label">Scroll</span>
          <div className="scroll-line" />
        </div>
      </section>
    </div>
  );
}